<script setup lang="ts">
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faArrowRight } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useImage from '~/composables/useImage'
import { useContentType } from '~/composables/useContentType'
import { useContentHelper } from '~/composables/useContentHelper'
import { useFavoritesApi } from '~/api/favoritesApi'
import useContentApi from '~/api/contentApi'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'

const { section } = defineProps<{ section: ContentSectionStandalone }>()

const { buildResourceUrl } = useUrlBuilder()
const { getName } = useContentType()
const { findContents } = useContentApi()
const { getImageSrc, getImageAltText } = useImage()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { isFile, isPdf } = useContentHelper()
const { getFavoriteList } = useFavoritesApi()
const { t } = useI18n()
const router = useRouter()

const { data: resource } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!section.resource?.destinationContentId),
  queryKey: computed(() => ['section-standalone', section.contentId]),
  queryFn: async () => (await findContents({
    contentIdCriterion: [Number(section.resource!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1))[0] ?? null,
  staleTime: Infinity,
  retry: 1,
})

const { data: list } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!section.listId),
  queryKey: computed(() => ['favorite-list', section.listId]),
  queryFn: () => getFavoriteList(section.listId!),
  staleTime: Infinity,
  retry: 1,
})

const listRoute = computed(() => list.value ? router.resolve({ name: 'favorite_list', params: { listId: list.value.id } }).href : '')
const link = computed(() => resource?.value ? buildResourceUrl(resource.value) : listRoute.value)

const isAnchor = computed(() => !link.value.startsWith('/'))
const transitionEffect = computed(() => !resource.value || (!isFile(resource.value) && !isPdf(resource.value)))
const contentTypeName = computed(() => resource?.value ? getName(resource.value) : list?.value ? t('favorite.list') : '')

const image = computed(() => ({
  src: Number(section.image?.destinationContentId) > 0 ? getImageSrc(section.image!, 'thumbnail_large') : resource.value?.thumbnail,
  alt: Number(section.image?.destinationContentId) > 0 ? getImageAltText(section.image!) : ''
}))
</script>

<template>
  <article
    v-if="resource || list"
    class="max-w-screen-au mx-auto size-full px-4 sm:px-8"
  >
    <LinkOrAnchor
      :link
      :is-anchor
      :transition-effect
      class="
        group border-gray-10 flex min-h-[300px] w-full flex-col overflow-hidden
        rounded-2xl border-2 transition-all hover:shadow-lg focus-visible:ring sm:flex-row
      "
      :class="{ 'sm:flex-row-reverse': section.alignRight }"
    >
      <div
        class="aspect-video max-h-[250px] w-full min-w-full sm:max-h-full sm:min-w-0"
        :style="`background: ${section.color[0] ? section.color[0] : 'var(--color-gray-40)'}`"
      >
        <img
          v-if="image.src"
          :src="image.src"
          class="block size-full overflow-hidden object-cover object-center"
          :alt="image.alt"
        >
      </div>
      <div class="flex w-full flex-col justify-center bg-white p-6 transition-colors sm:p-10">
        <p
          v-if="contentTypeName"
          class="text-seagreen-40 mb-4 font-semibold"
          v-text="contentTypeName"
        />
        <h2 class="mb-2 text-2xl font-bold text-blue-50 sm:text-3xl">
          <span class="relative block w-fit">
            {{ section.title }}
            <font-awesome-icon
              :icon="faArrowRight"
              class="mb-1 ml-1 inline-block transition-all group-hover:ml-2 group-focus-visible:ml-2"
            />
          </span>
        </h2>
        <p
          class="text-gray-40 text-base md:w-5/6"
          v-html="section.description"
        />
      </div>
    </LinkOrAnchor>
  </article>
</template>
